import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ExternalLink, RefreshCw, Sparkles, X } from "lucide-react";
import clsx from "clsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useAuthStore } from "@/store/auth";
import { useClients } from "@/features/clients/hooks/useClients";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import {
  createOffice,
  suggestStarostwo,
  type StarostwoProposal,
  type StarostwoSuggestion,
} from "../../api";
import { useSelectableOffices, useTaskPolling, useWypisOrderMutations } from "../../hooks";
import { apiErr } from "../../utils";
import { TaskLog } from "../ui";
import type { WypisApplicant, WypisCandidateGroup, WypisCandidateParcel } from "../../types";

const SUBJECT_OPTIONS: { key: string; label: string }[] = [
  { key: "wypis_rejestr_gruntow", label: "Wypis z rejestru gruntów" },
  { key: "wypis_bez_danych_osobowych", label: "Wypis z rejestru gruntów bez danych osobowych" },
  { key: "uproszczony_wypis", label: "Uproszczony wypis z rejestru gruntów" },
  { key: "wypis_i_wyrys", label: "Wypis z rejestru gruntów oraz wyrys z mapy ewidencyjnej" },
  { key: "wyrys_z_mapy", label: "Wyrys z mapy ewidencyjnej" },
  { key: "wypis_wykaz_dzialek", label: "Wypis z wykazu działek ewidencyjnych" },
  { key: "wypis_wykaz_podmiotow", label: "Wypis z wykazu podmiotów" },
];

const JUSTIFICATION_OPTIONS = [
  {
    value: "operator_systemu",
    label: "Jest operatorem systemu przesyłowego/dystrybucyjnego (Prawo energetyczne)",
  },
  {
    value: "operator_sieci",
    label: "Jest operatorem sieci (ustawa o wspieraniu rozwoju usług i sieci telekomunikacyjnych)",
  },
  { value: "interes_prawny", label: "Ma interes prawny w dostępie do danych" },
  { value: "wlasciciel", label: "Jest właścicielem / władającym gruntami objętymi wnioskiem" },
  {
    value: "organ_administracji",
    label: "Jest organem administracji publicznej lub podmiotem realizującym zadania publiczne",
  },
  { value: "inne", label: "Inne (tekst własny)" },
];

// Mirrors backend wypisy/constants.py LEGAL_INTEREST_BASIS/_CONTEXT — the
// field-tested section-10 formula for ELEKTIM applying in its own name.
const LEGAL_INTEREST_BASIS =
  "art. 17 w związku z art. 28 ust. 2 oraz art. 20 ust. 1 pkt 2 ustawy z dnia " +
  "7 lipca 1994 r. Prawo budowlane (Dz.U. z 2018 r. poz. 1202 ze zm.)";
const LEGAL_INTEREST_CONTEXT = "potrzebą opracowania dokumentacji projektowej";

// One order = one powiat: the modal is opened from a county group in the candidates
// panel with the checked parcels preselected; parcels can only be removed here.
export function CreateWypisOrderModal({
  group,
  parcels,
  onClose,
}: {
  group: WypisCandidateGroup;
  parcels: WypisCandidateParcel[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user);
  const { data: offices } = useSelectableOffices();
  const { data: users } = useInternalUsers();
  const { create } = useWypisOrderMutations();

  const qc = useQueryClient();
  const [selected, setSelected] = useState<WypisCandidateParcel[]>(parcels);
  const [starostwo, setStarostwo] = useState("");
  // Domyślny wnioskodawca z operatora (OSD) pierwszej działki: TAURON → TAURON,
  // reszta → ENERGA (dotychczasowy default). ELEKTIM wybiera się ręcznie.
  const [applicant, setApplicant] = useState<WypisApplicant>(
    parcels[0]?.operator === "tauron" ? "tauron" : "energa",
  );
  // Wnioskodawca „inwestor": zwykle klient przedsięwzięcia (stąd podpowiedź z działki),
  // ale bywa podmiot spoza kartoteki — wtedy dane wpisuje się z ręki.
  const [applicantClient, setApplicantClient] = useState(parcels[0]?.client_id ?? "");
  const [investorManual, setInvestorManual] = useState(false);
  const [investorName, setInvestorName] = useState("");
  const [investorAddress, setInvestorAddress] = useState("");
  const [investorNip, setInvestorNip] = useState("");
  const [investorRegon, setInvestorRegon] = useState("");
  // Kartotekę klientów pobieramy dopiero, gdy wnioskodawcą ma być inwestor — reszta
  // zamówień jej nie potrzebuje.
  const { data: clients } = useClients(applicant === "inwestor");
  const [contactPerson, setContactPerson] = useState(me?.id ? String(me.id) : "");
  const [subjectScope, setSubjectScope] = useState<string[]>(["uproszczony_wypis"]);
  const [justificationKind, setJustificationKind] = useState("operator_systemu");
  const [justificationText, setJustificationText] = useState("");
  const [justificationBasis, setJustificationBasis] = useState(LEGAL_INTEREST_BASIS);
  const [justificationContext, setJustificationContext] = useState(LEGAL_INTEREST_CONTEXT);
  const [documentForm, setDocumentForm] = useState("elektroniczna");
  const [pickupMethod, setPickupMethod] = useState("epuap");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  // Order was created outside the company ordering days: we don't leave the modal
  // immediately, we show the soft reminder first and let the user go on.
  const [created, setCreated] = useState<{ id: string; warnings: string[] } | null>(null);

  // AI lookup of the EGiB office: registry hits answer instantly; otherwise a
  // background task runs (TERYT prefix + web search) and its log streams here as
  // live progress. A proposal opens an editable mini-form — only the explicit
  // button creates the Office.
  const [aiNote, setAiNote] = useState("");
  const [proposal, setProposal] = useState<StarostwoProposal | null>(null);
  const [lookupTaskId, setLookupTaskId] = useState<string | null>(null);
  const lookupTask = useTaskPolling(lookupTaskId);
  const lookupRunning =
    !!lookupTaskId && !["success", "failure", "cancelled"].includes(lookupTask.data?.status ?? "");

  const applySuggestion = (result: StarostwoSuggestion) => {
    if (result.existing.length > 0) {
      setProposal(null);
      setStarostwo(result.existing[0].id);
      setAiNote(`Znaleziono w rejestrze: ${result.existing[0].name}`);
    } else if (result.proposal) {
      setProposal(result.proposal);
      setAiNote(
        result.proposal.via_web_search
          ? "Propozycja z wyszukiwania w internecie — zweryfikuj przed dodaniem."
          : "Propozycja z wiedzy modelu (bez wyszukiwania) — koniecznie zweryfikuj.",
      );
    } else {
      setAiNote("Nie udało się znaleźć urzędu — dodaj go ręcznie.");
    }
  };

  const findStarostwo = useMutation({
    mutationFn: () =>
      suggestStarostwo(group.county, group.voivodeship, selected.map((p) => p.teryt)),
    onSuccess: (result) => {
      if (result.task_id) {
        setLookupTaskId(result.task_id);
      } else {
        applySuggestion(result);
      }
    },
    onError: () => setAiNote("Wyszukiwanie nie powiodło się — dodaj urząd ręcznie."),
  });

  // The background lookup finished — read the suggestion from the task result.
  useEffect(() => {
    if (!lookupTaskId || !lookupTask.data) return;
    if (lookupTask.data.status === "success") {
      applySuggestion((lookupTask.data.result ?? { existing: [], proposal: null }) as StarostwoSuggestion);
      setLookupTaskId(null);
    } else if (["failure", "cancelled"].includes(lookupTask.data.status)) {
      setAiNote("Wyszukiwanie nie powiodło się — dodaj urząd ręcznie.");
      setLookupTaskId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupTask.data?.status]);
  const addProposedOffice = useMutation({
    mutationFn: () =>
      createOffice({
        name: proposal!.name,
        kind: "starostwo",
        department: proposal!.department,
        street: proposal!.street,
        building_no: proposal!.building_no,
        postal_code: proposal!.postal_code,
        city: proposal!.city,
        phone: proposal!.phone,
        email: proposal!.email,
        bank_account: proposal!.bank_account,
        notes: [
          proposal!.website ? `WWW: ${proposal!.website}` : "",
          proposal!.bank_account_source ? `Źródło numeru konta: ${proposal!.bank_account_source}` : "",
          proposal!.source ? `Dodano z wyszukiwania AI (źródło: ${proposal!.source})` : "Dodano z wyszukiwania AI",
        ].filter(Boolean).join("\n"),
      }),
    onSuccess: (office) => {
      qc.invalidateQueries({ queryKey: ["pp", "offices"] });
      setStarostwo(office.id);
      setProposal(null);
      setAiNote(`Dodano i wybrano: ${office.name}`);
    },
    onError: () => setAiNote("Nie udało się dodać urzędu — uzupełnij brakujące pola adresu."),
  });
  const setProposalField = (k: keyof StarostwoProposal, v: string) =>
    setProposal((p) => (p ? { ...p, [k]: v } : p));

  const starostwoOptions = useMemo(
    () =>
      (offices ?? [])
        .filter((o) => o.kind === "starostwo")
        .map((o) => ({ value: o.id, label: o.name })),
    [offices],
  );
  const userOptions = useMemo(
    () => (users ?? []).map((u) => ({ value: String(u.id), label: u.full_name || u.username })),
    [users],
  );
  const clientOptions = useMemo(
    () =>
      (clients ?? [])
        .filter((c) => c.is_active)
        .map((c) => ({ value: c.id, label: `${c.name} (NIP ${c.nip})` })),
    [clients],
  );
  const pickedClient = useMemo(
    () => (clients ?? []).find((c) => c.id === applicantClient) ?? null,
    [clients, applicantClient],
  );
  // Wniosek bez wnioskodawcy urząd odrzuca — blokujemy wysyłkę, zamiast pozwolić
  // wygenerować druk z pustym polem 1.
  const investorMissing =
    applicant === "inwestor" && (investorManual ? !investorName.trim() : !applicantClient);

  const toggleSubject = (key: string) =>
    setSubjectScope((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const submit = () => {
    setError("");
    create.mutate(
      {
        county: group.county,
        voivodeship: group.voivodeship,
        parcel_ids: selected.map((p) => p.id),
        starostwo,
        applicant,
        // Tożsamość inwestora do pola 1 — albo klient z kartoteki, albo dane z ręki.
        // Nieużywaną stronę czyścimy, żeby na zamówieniu nie zostały dwie wersje prawdy.
        ...(applicant === "inwestor"
          ? {
              applicant_client: investorManual ? null : applicantClient,
              applicant_custom_name: investorManual ? investorName.trim() : "",
              applicant_custom_address: investorManual ? investorAddress.trim() : "",
              applicant_custom_nip: investorManual ? investorNip.trim() : "",
              applicant_custom_regon: investorManual ? investorRegon.trim() : "",
            }
          : {}),
        contact_person: contactPerson || undefined,
        subject_scope: subjectScope,
        justification_kind: justificationKind,
        justification_text: justificationText || undefined,
        justification_basis:
          justificationKind === "interes_prawny" ? justificationBasis || undefined : undefined,
        justification_context:
          justificationKind === "interes_prawny" ? justificationContext || undefined : undefined,
        document_form: documentForm,
        pickup_method: pickupMethod,
        notes: notes || undefined,
      },
      {
        onSuccess: ({ data: order, warnings }) => {
          if (warnings.length) setCreated({ id: order.id, warnings });
          else navigate(`/power-projects/wypisy/${order.id}`);
        },
        onError: (e: unknown) => {
          setError(apiErr(e, "Nie udało się utworzyć zamówienia."));
        },
      },
    );
  };

  return (
    <Modal open onClose={onClose} title={`Zamów wypisy — powiat ${group.county}`} size="lg">
      <div className="space-y-4">
        <div>
          <span className="section-label">Działki ({selected.length})</span>
          <div className="mt-1 flex flex-wrap gap-1 max-h-32 overflow-y-auto">
            {selected.map((p) => (
              <span
                key={p.id}
                className={clsx(
                  "inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-mono border",
                  // Działka ze zgłoszenia „potrzebny wypis" ma już wpisanego właściciela —
                  // wyróżniamy ją, bo to jedyna w paczce, której zwykły automat by nie wziął.
                  p.wypis_needed && (p.wypisy_total > 0 || p.wypis_orders_total > 0)
                    ? "border-orange-300 bg-orange-50 text-orange-800"
                    : p.wypis_needed && p.owners_total > 0
                      ? "border-amber-300 bg-amber-50 text-amber-800"
                      : "border-[var(--color-border)] bg-[var(--color-bg-elevated)]",
                )}
                title={
                  `${p.project_name} · obręb ${p.obreb || "—"}`
                  + (p.wypis_needed && (p.wypisy_total > 0 || p.wypis_orders_total > 0)
                    ? ` · PONOWNE zamówienie (${p.wypisy_total > 0 ? "ma już wypis" : "wypis odpięty"}), wpisanych właścicieli: ${p.owners_total}`
                    : p.wypis_needed && p.owners_total > 0
                      ? ` · zgłoszona ręcznie („potrzebny wypis”), ma już wpisanych właścicieli: ${p.owners_total}`
                      : "")
                  + (p.wypis_needed && p.wypis_needed_reason ? ` · powód: ${p.wypis_needed_reason}` : "")
                }
              >
                {p.teryt}
                <button
                  className="text-[var(--color-text-muted)] hover:text-red-600"
                  onClick={() => setSelected((s) => s.filter((x) => x.id !== p.id))}
                  disabled={selected.length <= 1}
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="block">
            <span className="section-label">Adresat (starostwo) *</span>
            <div className="flex items-center gap-1 mt-1">
              <div className="flex-1">
                <SearchableSelect
                  options={starostwoOptions}
                  value={starostwo}
                  placeholder="Wybierz starostwo…"
                  onChange={(e) => setStarostwo(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="p-1.5 border border-[var(--color-border-strong)] hover:bg-[var(--color-bg-elevated)]"
                title="Odśwież listę urzędów"
                onClick={() => qc.invalidateQueries({ queryKey: ["pp", "offices"] })}
              >
                <RefreshCw size={13} />
              </button>
              <button
                type="button"
                className="p-1.5 border border-[var(--color-border-strong)] hover:bg-[var(--color-bg-elevated)]"
                title="Dodaj urząd w nowej karcie (rejestr urzędów)"
                onClick={() => window.open("/paper-send/offices", "_blank")}
              >
                <ExternalLink size={13} />
              </button>
              <button
                type="button"
                className="p-1.5 border border-[var(--color-border-strong)] hover:bg-[var(--color-bg-elevated)] disabled:opacity-50"
                title="Znajdź urząd EGiB (AI: rejestr → TERYT działek → internet)"
                disabled={findStarostwo.isPending || lookupRunning}
                onClick={() => { setAiNote(""); setProposal(null); findStarostwo.mutate(); }}
              >
                <Sparkles size={13} className={findStarostwo.isPending || lookupRunning ? "animate-pulse" : ""} />
              </button>
            </div>
            {(findStarostwo.isPending || lookupRunning) && (
              <div className="text-[11px] text-[var(--color-text-muted)] mt-1 space-y-0.5">
                <div className="flex items-center gap-1">
                  <Sparkles size={11} className="animate-pulse" />
                  Szukam urzędu EGiB po identyfikatorach działek…
                </div>
                {/* Live progress: the background task's full log, refreshed by polling. */}
                <TaskLog log={lookupTask.data?.log ?? ""} />
              </div>
            )}
            {aiNote && !findStarostwo.isPending && !lookupRunning && (
              <div className="text-[11px] text-[var(--color-text-secondary)] mt-1">{aiNote}</div>
            )}
          </div>
          <label className="block">
            <span className="section-label">Osoba do kontaktów</span>
            <SearchableSelect
              options={userOptions}
              value={contactPerson}
              placeholder="Wybierz osobę…"
              onChange={(e) => setContactPerson(e.target.value)}
            />
          </label>
        </div>

        {proposal && (
          <div className="border border-dashed border-[var(--color-border-strong)] p-3 space-y-2">
            <span className="section-label flex items-center gap-1">
              <Sparkles size={12} /> Proponowane dane urzędu — zweryfikuj i dodaj
            </span>
            {(proposal.contact_url || proposal.website || proposal.source) && (
              <a
                href={proposal.contact_url || proposal.website || proposal.source}
                target="_blank"
                rel="noreferrer"
                title={proposal.contact_url || proposal.website || proposal.source}
                className="inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline"
              >
                <ExternalLink size={11} /> Sprawdź adres i kontakt na stronie urzędu
              </a>
            )}
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="section-label">Nazwa urzędu *</span>
                <input className="input-base mt-0.5 text-xs" value={proposal.name}
                  onChange={(e) => setProposalField("name", e.target.value)} />
              </label>
              <label className="block">
                <span className="section-label">Wydział (EGiB / geodezja)</span>
                <input className="input-base mt-0.5 text-xs" value={proposal.department}
                  placeholder="np. Wydział Geodezji, Kartografii i Katastru"
                  onChange={(e) => setProposalField("department", e.target.value)} />
              </label>
              <label className="block">
                <span className="section-label">Ulica *</span>
                <input className="input-base mt-0.5 text-xs" value={proposal.street}
                  onChange={(e) => setProposalField("street", e.target.value)} />
              </label>
              <label className="block">
                <span className="section-label">Nr budynku *</span>
                <input className="input-base mt-0.5 text-xs" value={proposal.building_no}
                  onChange={(e) => setProposalField("building_no", e.target.value)} />
              </label>
              <label className="block">
                <span className="section-label">Kod pocztowy *</span>
                <input className="input-base mt-0.5 text-xs font-mono" placeholder="00-000" value={proposal.postal_code}
                  onChange={(e) => setProposalField("postal_code", e.target.value)} />
              </label>
              <label className="block">
                <span className="section-label">Miejscowość *</span>
                <input className="input-base mt-0.5 text-xs" value={proposal.city}
                  onChange={(e) => setProposalField("city", e.target.value)} />
              </label>
              <label className="block">
                <span className="section-label">Telefon</span>
                <input className="input-base mt-0.5 text-xs" value={proposal.phone}
                  onChange={(e) => setProposalField("phone", e.target.value)} />
              </label>
              <label className="block">
                <span className="section-label">E-mail</span>
                <input className="input-base mt-0.5 text-xs" value={proposal.email}
                  onChange={(e) => setProposalField("email", e.target.value)} />
              </label>
              <label className="block col-span-2">
                <span className="section-label">Numer konta (opłaty za wypisy)</span>
                <input className="input-base mt-0.5 text-xs font-mono" placeholder="26 cyfr (PL…)" value={proposal.bank_account}
                  onChange={(e) => setProposalField("bank_account", e.target.value)} />
                {proposal.bank_account_source && (
                  <a href={proposal.bank_account_source} target="_blank" rel="noreferrer"
                     title={proposal.bank_account_source}
                     className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline">
                    <ExternalLink size={11} /> Źródło numeru konta
                  </a>
                )}
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setProposal(null)}>Odrzuć</Button>
              <Button
                size="sm"
                loading={addProposedOffice.isPending}
                disabled={!proposal.name || !proposal.street || !proposal.building_no || !proposal.postal_code || !proposal.city}
                onClick={() => addProposedOffice.mutate()}
              >
                Dodaj urząd i wybierz
              </Button>
            </div>
          </div>
        )}

        <div>
          <span className="section-label">Wnioskodawca</span>
          <div className="mt-1 flex flex-wrap gap-4">
            {([
              ["elektim", "ELEKTIM (my)"],
              ["energa", "ENERGA-OPERATOR"],
              ["tauron", "TAURON Dystrybucja"],
              ["inwestor", "Inwestor"],
            ] as [WypisApplicant, string][]).map(([value, label]) => (
              <label key={value} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  checked={applicant === value}
                  onChange={() => {
                    setApplicant(value);
                    // Ani ELEKTIM, ani inwestor nie są operatorem systemu — deklaracja
                    // „operator systemu" byłaby na ich wniosku nieprawdą, więc sekcja 10
                    // idzie za wnioskodawcą (nadal do zmiany ręcznie niżej).
                    setJustificationKind(
                      value === "energa" || value === "tauron"
                        ? "operator_systemu"
                        : "interes_prawny",
                    );
                  }}
                />
                {label}
              </label>
            ))}
          </div>
          {applicant === "inwestor" && (
            <div className="mt-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3 space-y-2">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={investorManual}
                  onChange={(e) => setInvestorManual(e.target.checked)}
                />
                Inwestora nie ma w kartotece klientów — wpiszę dane ręcznie
              </label>
              {investorManual ? (
                <div className="grid grid-cols-2 gap-2">
                  <label className="block col-span-2">
                    <span className="section-label">Nazwa inwestora *</span>
                    <input
                      className="input-base mt-0.5 text-xs"
                      value={investorName}
                      onChange={(e) => setInvestorName(e.target.value)}
                    />
                  </label>
                  <label className="block col-span-2">
                    <span className="section-label">Adres (jak na wniosku)</span>
                    <input
                      className="input-base mt-0.5 text-xs"
                      placeholder="ul. Technologiczna 2, 45-839 Opole"
                      value={investorAddress}
                      onChange={(e) => setInvestorAddress(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="section-label">NIP</span>
                    <input
                      className="input-base mt-0.5 text-xs font-mono"
                      value={investorNip}
                      onChange={(e) => setInvestorNip(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="section-label">REGON</span>
                    <input
                      className="input-base mt-0.5 text-xs font-mono"
                      value={investorRegon}
                      onChange={(e) => setInvestorRegon(e.target.value)}
                    />
                  </label>
                </div>
              ) : (
                <label className="block">
                  <span className="section-label">Inwestor (klient z kartoteki) *</span>
                  <SearchableSelect
                    options={clientOptions}
                    value={applicantClient}
                    placeholder="Wybierz klienta…"
                    onChange={(e) => setApplicantClient(e.target.value)}
                  />
                </label>
              )}
              {/* Co dokładnie stanie w polu 1 wniosku — pokazujemy przed generacją,
                  bo to jedyne miejsce, gdzie widać dane zaciągnięte z kartoteki. */}
              {!investorManual && pickedClient && (
                <div className="text-[11px] text-[var(--color-text-secondary)]">
                  W polu 1 wniosku: <b>{pickedClient.name}</b>
                  {pickedClient.registered_address
                    ? ` · ul. ${pickedClient.registered_address.street} ${pickedClient.registered_address.building_number}`
                      + `, ${pickedClient.registered_address.postal_code} ${pickedClient.registered_address.city}`
                    : ""}
                  {pickedClient.nip ? ` · NIP ${pickedClient.nip}` : ""}
                </div>
              )}
              {investorMissing && (
                <div className="text-[11px] text-red-600">
                  {investorManual
                    ? "Wpisz nazwę inwestora — bez niej pole 1 wniosku byłoby puste."
                    : "Wybierz inwestora z kartoteki albo zaznacz wpisywanie danych ręcznie."}
                </div>
              )}
              <div className="text-[11px] text-[var(--color-text-muted)]">
                Opłata skarbowa za pełnomocnictwo (17 zł) pójdzie do urzędu miasta/gminy
                siedziby inwestora — sprawdź ją na zamówieniu przed wysyłką.
              </div>
            </div>
          )}
        </div>

        <div>
          <span className="section-label">Przedmiot wniosku (sekcja 8)</span>
          <div className="mt-1 grid grid-cols-2 gap-1">
            {SUBJECT_OPTIONS.map((opt) => (
              <label key={opt.key} className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={subjectScope.includes(opt.key)}
                  onChange={() => toggleSubject(opt.key)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="section-label">Uzasadnienie (sekcja 10)</span>
            <select
              className="input-base mt-1"
              value={justificationKind}
              onChange={(e) => setJustificationKind(e.target.value)}
            >
              {JUSTIFICATION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="section-label">Postać dokumentów</span>
              <select
                className="input-base mt-1"
                value={documentForm}
                onChange={(e) => setDocumentForm(e.target.value)}
              >
                <option value="papierowa">Papierowa</option>
                <option value="elektroniczna">Elektroniczna</option>
              </select>
            </label>
            <label className="block">
              <span className="section-label">Sposób odbioru</span>
              <select
                className="input-base mt-1"
                value={pickupMethod}
                onChange={(e) => setPickupMethod(e.target.value)}
              >
                <option value="poczta">Wysyłka pocztą</option>
                <option value="odbior_osobisty">Odbiór osobisty</option>
                <option value="epuap">e-Doręczenia</option>
              </select>
            </label>
          </div>
        </div>

        {justificationKind === "interes_prawny" && (
          <div className="grid gap-2">
            <label className="block">
              <span className="section-label">Interes prawny wynikający z (pole 12)</span>
              <textarea
                className="input-base mt-1 h-16"
                value={justificationBasis}
                onChange={(e) => setJustificationBasis(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="section-label">w związku z (pole 13)</span>
              <textarea
                className="input-base mt-1 h-10"
                value={justificationContext}
                onChange={(e) => setJustificationContext(e.target.value)}
              />
            </label>
          </div>
        )}
        {justificationKind === "inne" && (
          <label className="block">
            <span className="section-label">Uzasadnienie — tekst (puste = szablon o projektowaniu sieci)</span>
            <textarea
              className="input-base mt-1 h-16"
              value={justificationText}
              onChange={(e) => setJustificationText(e.target.value)}
            />
          </label>
        )}

        <label className="block">
          <span className="section-label">Uwagi (sekcja 13)</span>
          <textarea className="input-base mt-1 h-14" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        {error && <div className="text-xs text-red-600">{error}</div>}

        {created && (
          <div className="flex gap-2 border border-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div className="space-y-1">
              {created.warnings.map((w, i) => <p key={i}>{w}</p>)}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          {created ? (
            <Button onClick={() => navigate(`/power-projects/wypisy/${created.id}`)}>
              Przejdź do zamówienia
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={onClose}>Anuluj</Button>
              <Button
                onClick={submit}
                loading={create.isPending}
                disabled={
                  !starostwo || selected.length === 0 || subjectScope.length === 0 || investorMissing
                }
              >
                Utwórz zamówienie ({selected.length})
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
